import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { getBelgelerPage, STRAPI_URL } from '../services/api';
import ScrollReveal from '../components/ScrollReveal';
import Seo from '../components/Seo';
import DEFAULT_DATA from '../default_datas/pages/defaultBelgeler';


const resolveMediaUrl = (mediaField) => {
  if (!mediaField) return null;
  const media = Array.isArray(mediaField) ? mediaField[0] : mediaField;
  const url = media?.url;
  if (!url) return null;
  return url.startsWith('http') ? url : `${STRAPI_URL}${url}`;
};

const Hero = ({ data, tx, lang, navigate }) => {
  const d = { ...DEFAULT_DATA.hero, ...data };
  const imageUrl = resolveMediaUrl(d.image);

  return (
    <section className="max-w-[1280px] mx-auto px-6 lg:px-10 py-10 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
      <div className="flex flex-col gap-4 lg:gap-6 text-center lg:text-left items-center lg:items-start">
        <span className="inline-block px-3 py-1 bg-[rgba(15,189,73,0.1)] text-[#4BA18C] text-[10px] lg:text-xs font-bold tracking-[2px] uppercase rounded-full w-fit">
          {tx(d.badge, d.badge_en)}
        </span>

        <h1 className="text-3xl lg:text-6xl font-black leading-tight text-[#111813] m-0">
          {tx(d.title, d.title_en)}
        </h1>

        <p className="text-sm lg:text-lg leading-relaxed text-[#4BA18C] m-0 max-w-xl">
          {tx(d.description, d.description_en)}
        </p>

        <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
          <button
            onClick={() => navigate('/iletisim')}
            className="h-12 lg:h-14 px-8 text-sm lg:text-base font-bold rounded-xl bg-[#4BA18C] text-[#111813] border-none cursor-pointer hover:scale-105 transition-transform w-full sm:w-auto"
          >
            {tx(d.primaryButtonText, d.primaryButtonText_en)}
          </button>

          {d.secondaryButtonText && (
            <button
              onClick={() => navigate('/iletisim')}
              className="h-12 lg:h-14 px-8 text-sm lg:text-base font-bold rounded-xl bg-white text-[#111813] border border-[#e8eee9] cursor-pointer hover:scale-105 transition-transform w-full sm:w-auto"
            >
              {tx(d.secondaryButtonText, d.secondaryButtonText_en)}
            </button>
          )}
        </div>
      </div>

      <div className="w-full aspect-video rounded-xl lg:rounded-2xl overflow-hidden shadow-2xl border border-[#e8eee9] relative bg-[#e8eee9]">
        {imageUrl ? (
          <img
            src={imageUrl}
            className="absolute inset-0 w-full h-full object-cover"
            alt={tx(d.title, d.title_en)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs lg:text-sm">
            {lang === 'en' ? 'Image Not Loaded' : 'Görsel Yüklenemedi'}
          </div>
        )}
      </div>
    </section>
  );
};

const Features = ({ data, tx, lang }) => {
  const d = { ...DEFAULT_DATA.features, ...data };

  return (
    <section className="bg-white py-12 lg:py-20">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-10 lg:mb-12 text-center lg:text-left mx-auto lg:mx-0">
          <span className="block text-[10px] lg:text-xs font-bold tracking-[2px] lg:tracking-[3px] uppercase text-[#4BA18C] mb-3">
            {lang === 'en' ? 'Features' : 'Özellikler'}
          </span>

          <h2 className="text-2xl lg:text-4xl font-black text-[#111813] mb-4">
            {tx(d.Title, d.Title_en)}
          </h2>

          <p className="text-sm lg:text-lg text-[#61896f] leading-relaxed m-0">
            {tx(d.Description, d.Description_en)}
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {d.cards?.map((f, i) => (
            <div
              key={i}
              className="flex flex-col gap-4 lg:gap-6 rounded-xl lg:rounded-2xl border border-[#e8eee9] bg-[#f6f8f6] p-5 lg:p-8 hover:border-[rgba(113,131,119,0.5)] transition-colors group"
            >
              <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-lg lg:rounded-xl bg-[rgba(15,189,73,0.1)] text-[#4BA18C] flex items-center justify-center group-hover:bg-[#4BA18C] group-hover:text-white transition-colors">
                <span className="material-symbols-outlined text-xl lg:text-2xl">
                  {f.icon || 'star'}
                </span>
              </div>

              <div>
                <h4 className="text-xs lg:text-lg font-bold text-[#111813] mb-1.5 lg:mb-2">
                  {tx(f.Title || f.title, f.Title_en || f.title_en)}
                </h4>

                <p className="text-[10px] lg:text-sm text-[#4BA18C] leading-relaxed m-0">
                  {tx(f.Description || f.description, f.Description_en || f.description_en)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Modules = ({ data, tx, lang }) => {
  const d = { ...DEFAULT_DATA.modules, ...data };

  return (
    <section className="py-12 lg:py-20 bg-[#f6f8f6]">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <h2 className="text-2xl lg:text-4xl font-black text-[#111813] mb-8 lg:mb-12 text-center lg:text-left">
          {tx(d.Title, d.Title_en)}
        </h2>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          {d.modules?.map((m, i) => {
            const imageUrl = resolveMediaUrl(m.image);

            return (
              <div key={i} className="group">
                <div className="w-full aspect-video rounded-xl lg:rounded-2xl overflow-hidden mb-4 lg:mb-6 relative bg-[#e8eee9] border border-[#e8eee9]">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      alt={tx(m.Text, m.Text_en) || 'Module'}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs lg:text-sm">
                      {lang === 'en' ? 'Image Not Loaded' : 'Görsel Yüklenemedi'}
                    </div>
                  )}
                </div>

                <h3 className="text-lg lg:text-2xl font-bold mb-2 lg:mb-3 text-[#111813]">
                  {tx(m.Text, m.Text_en)}
                </h3>

                <p className="text-sm lg:text-base text-[#61896f] leading-relaxed m-0">
                  {tx(m.Description, m.Description_en)}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const CTA = ({ data, tx, navigate }) => {
  const d = { ...DEFAULT_DATA.cta, ...data };

  return (
    <section className="bg-white py-10 lg:py-20 px-6">
      <div className="max-w-[1280px] mx-auto bg-[#4BA18C] rounded-2xl lg:rounded-[3rem] p-6 lg:p-20 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 lg:gap-10">
        <div className="max-w-2xl text-left">
          <h2 className="text-xl lg:text-5xl font-black text-[#111813] mb-2 lg:mb-4 leading-tight">
            {tx(d.Title || d.title, d.Title_en || d.title_en)}
          </h2>

          <p className="text-xs lg:text-lg text-[#111813] opacity-90 leading-relaxed">
            {tx(d.Description || d.description, d.Description_en || d.description_en)}
          </p>
        </div>

        <div className="flex gap-3 w-full lg:w-auto">
          <button
            onClick={() => navigate('/iletisim')}
            className="h-12 lg:h-14 px-6 lg:px-10 text-sm lg:text-base font-bold rounded-xl bg-[#111813] text-[#4BA18C] border-none cursor-pointer hover:scale-105 transition-transform flex-1 lg:flex-none"
          >
            {tx(d.primaryButtonText, d.primaryButtonText_en)}
          </button>
        </div>
      </div>
    </section>
  );
};

const Belgeler = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  const tx = (tr, en) => (lang === 'en' ? (en || tr) : tr);

  useEffect(() => {
    getBelgelerPage()
      .then((res) => {
        if (res) setPageData(res);
      })
      .catch((err) => {
        console.error('Belgeler sayfası veri çekme hatası:', err);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-[#4BA18C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!pageData || !pageData.pageContent?.length) {
    return (
      <div className="min-h-screen bg-[#f6f8f6] font-poppins overflow-x-hidden">
        <Seo
          title={lang === 'en' ? 'Document Management | RUZY' : 'Politika ve Belge Yönetimi | RUZY'}
          path="/belgeler"
        />
        <ScrollReveal><Hero data={{}} tx={tx} lang={lang} navigate={navigate} /></ScrollReveal>
        <ScrollReveal><Features data={{}} tx={tx} lang={lang} /></ScrollReveal>
        <ScrollReveal><Modules data={{}} tx={tx} lang={lang} /></ScrollReveal>
        <ScrollReveal><CTA data={{}} tx={tx} navigate={navigate} /></ScrollReveal>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f8f6] font-poppins overflow-x-hidden">
      <Seo
        title={lang === 'en' ? 'Document Management | RUZY' : 'Politika ve Belge Yönetimi | RUZY'}
        path="/belgeler"
      />

      {pageData.pageContent?.map((section, index) => (
        <ScrollReveal key={index}>
          {section.__component === 'sections.hero' && (
            <Hero data={section} tx={tx} lang={lang} navigate={navigate} />
          )}
          {section.__component === 'sections.features-section' && (
            <Features data={section} tx={tx} lang={lang} />
          )}
          {section.__component === 'sections.module-section' && (
            <Modules data={section} tx={tx} lang={lang} />
          )}
          {section.__component === 'sections.cta' && (
            <CTA data={section} tx={tx} navigate={navigate} />
          )}
        </ScrollReveal>
      ))}
    </div>
  );
};

export default Belgeler;